// Форматирование данных для отображения. Все даты считаются в МСК
// (TZ.md §9), независимо от локального часового пояса браузера.
//
// dayjs плагины extend'им явно прямо здесь. main.jsx тоже их подключает,
// но порядок import'ов между модулями зависит от bundler'а — а нам
// принципиально, чтобы .tz() работал ВСЕГДА, в любом порядке вызова.
// dayjs.extend идемпотентен, повторное подключение безопасно.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const MSK_TZ = 'Europe/Moscow';

/** ISO timestamp → "30.04.2026 14:23" в МСК. */
export function formatDateTime(iso) {
    if (!iso) return '—';
    return dayjs(iso).tz(MSK_TZ).format('DD.MM.YYYY HH:mm');
}

/** Алиас formatDateTime — явно подчёркивает, что отдаём время в МСК (TZ.md §9). */
export const formatMoscowDateTime = formatDateTime;

/** ISO timestamp → "30.04.2026" в МСК. */
export function formatDate(iso) {
    if (!iso) return '—';
    return dayjs(iso).tz(MSK_TZ).format('DD.MM.YYYY');
}

/** Сколько дней осталось до даты, относительно "сейчас" в МСК. <=0 если истекло. */
export function daysUntil(iso) {
    if (!iso) return null;
    const now = dayjs().tz(MSK_TZ);
    const target = dayjs(iso).tz(MSK_TZ);
    return Math.ceil(target.diff(now, 'day', true));
}

/** Секунды → "5 мин 30 сек" / "1 ч 12 мин". */
export function formatDuration(sec) {
    if (sec === null || sec === undefined) return '—';
    const s = Math.max(0, Math.floor(Number(sec)));
    if (s < 60) return `${s} сек`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m < 60) return rem ? `${m} мин ${rem} сек` : `${m} мин`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm ? `${h} ч ${mm} мин` : `${h} ч`;
}

/** Деньги в USD: $0.0123 / $1.23 / $123.45 — adaptive precision. */
export function formatUsd(value) {
    const n = Number(value || 0);
    if (n === 0) return '$0';
    if (Math.abs(n) < 1) return `$${n.toFixed(4)}`;
    if (Math.abs(n) < 100) return `$${n.toFixed(2)}`;
    return `$${Math.round(n)}`;
}

/** Деньги в RUB: "1,74 ₽" (русский локаль, всегда 2 знака после запятой). null/undefined → "—". */
export function formatRub(value) {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export const ROLE_META = {
    creator: { label: 'Создатель', color: 'grape' },
    curator: { label: 'Куратор', color: 'indigo' },
    admin: { label: 'Админ', color: 'blue' },
    rop: { label: 'РОП', color: 'teal' },
    mop: { label: 'МОП', color: 'gray' },
};

// Метаданные тарифных ПЛАНОВ (tariff_plan). Статус (active/expired) — отдельно.
export const TARIFF_PLAN_META = {
    trial: { label: 'Триал', color: 'yellow' },
    m200: { label: '200 минут', color: 'blue' },
    m600: { label: '600 минут', color: 'teal' },
    m1200: { label: '1200 минут', color: 'grape' },
    custom: { label: 'Индивидуальный', color: 'indigo' },
};

// День недели, weekday 1=Пн..7=Вс (как в /api/analytics).
export const WEEKDAY_SHORT = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/**
 * Цвет ячейки/бейджа по агрегированной оценке. Шкала 0..50.
 * Совпадает по семантике с другими шкалами в приложении (TariffBadge / DialogsListPage):
 *   ≥40 — зелёный, ≥25 — синий, ≥15 — оранжевый, иначе красный.
 */
export function scoreColor(score) {
    if (score === null || score === undefined || Number.isNaN(score)) return 'gray';
    if (score >= 40) return 'green';
    if (score >= 25) return 'blue';
    if (score >= 15) return 'orange';
    return 'red';
}

/**
 * Форматирует нормализованный российский номер 7XXXXXXXXXX → "+7 XXX XXX-XX-XX".
 * Если строка не соответствует — возвращает as-is.
 */
export function formatPhoneRu(digits) {
    if (typeof digits !== 'string') return '';
    const m = /^7(\d{3})(\d{3})(\d{2})(\d{2})$/.exec(digits);
    if (!m) return digits;
    return `+7 ${m[1]} ${m[2]}-${m[3]}-${m[4]}`;
}

/** "DD.MM" из YYYY-MM-DD без таймзонной возни (бэк уже отдал в МСК). */
export function formatDayShort(isoDate) {
    if (!isoDate || typeof isoDate !== 'string') return '—';
    const [, m, d] = isoDate.split('-');
    return `${d}.${m}`;
}

/** Выходной ли день (weekday: 6=Сб, 7=Вс). */
export function isWeekend(weekday) {
    return weekday === 6 || weekday === 7;
}
